import { formatDistanceToNow, parseISO } from 'date-fns';

// ----------------------------------------------------------------------
export function fToNow(date: Date | string | number) {
    return formatDistanceToNow(new Date(date), {
        addSuffix: true,
    });
}

export function pad(num: number, size: number) {
    var s = "000000000" + num.toString();
    return s.substr(s.length - size);
}

export function getDateString(date: Date): string {
    const d = getDate(date);
    return `${pad(d.getDate(), 2)}/${pad(d.getMonth() + 1, 2)}/${d.getFullYear()}`;
}

export function getInvariantDateString(date: Date): string {
    const d = getDate(date);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1, 2)}-${pad(d.getDate(), 2)}`;
}

export function getDateShortString(date: Date | undefined): string {
    if (!date) return '--/--';
    const d = getDate(date);
    return `${pad(d.getDate(), 2)}/${pad(d.getMonth() + 1, 2)}`;
}

export function getDateShortYearString(date: Date | undefined | null): string {
    if (!date) return '--/--/--';
    const d = getDate(date);
    return `${pad(d.getDate(), 2)}/${pad(d.getMonth() + 1, 2)}/${d.getFullYear() - 2000}`;
}

export function getDate(date: Date|string): Date {
    if (typeof (date) !== 'string')
        return new Date(date);
    else
        return parseISO(date);
}

export function getInvariantDate(date: Date) {
    const d = getDate(date);
    return parseISO(`${d.getFullYear()}-${pad(d.getMonth() + 1, 2)}-${pad(d.getDate(), 2)}`);
}

export function getLongStringDate(date: Date) {
    const d = getDate(date);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1, 2)}-${pad(d.getDate(), 2)} ${getTime(date)}`;
}

export function getTime(date: Date) {
    const d = getDate(date);
    return `${pad(d.getHours(), 2)}:${pad(d.getMinutes(), 2)}:${pad(d.getSeconds(), 2)}.${d.getMilliseconds()}`;
}

export function getToday() {
    let today = new Date();
    return getInvariantDate(today);
}

export function addDays(date: Date, diffDays: number) {
    let d = getDate(date);
    return getInvariantDate(new Date(d.setDate(d.getDate() + diffDays)));
}

export function addMonths(date: Date, diffMonths: number) {
    let d = getDate(date);
    return getInvariantDate(new Date(d.setMonth(d.getMonth() + diffMonths)));
}

export function getPreviousBusinessDay(date?: Date) {
    let d = date ? addDays(date, -1) : addDays(getToday(), -1);
    let dayOfWeek = d.getDay();
    while (dayOfWeek === 0 || dayOfWeek === 6) {
        d = addDays(d, -1);
        dayOfWeek = d.getDay();
    }
    return d;
}

export function getNextBusinessDay(date?: Date) {
    let d = date ? addDays(date, 1) : addDays(getToday(), 1);
    let dayOfWeek = d.getDay();
    while (dayOfWeek === 0 || dayOfWeek === 6) {
        d = addDays(d, 1);
        dayOfWeek = d.getDay();
    }
    return d;
}

export const millisecondsToElapsed = (string: string | undefined) => {
    if (!string) return '';
    let time = parseFloat(string);
    let hours = Math.floor((time / (1000 * 60 * 60)) % 24);
    let minutes = Math.floor((time / 1000 / 60) % 60);
    let seconds = Math.floor((time / 1000) % 60);
    let milliseconds = Math.floor(time % 1000);

    return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)}.${pad(milliseconds, 3)}`;
}

export const millisecondsToShortElapsed = (string: string | undefined) => {
    if (!string) return '';
    let time = parseFloat(string);
    let minutes = Math.floor((time / 1000 / 60) % 60);
    let seconds = Math.floor((time / 1000) % 60);

    return `${pad(minutes, 2)}:${pad(seconds, 2)}`;
}

export function getMondayWeek(d: Date) {
    d = new Date(d);
    return new Date(d.setDate(d.getDate() - d.getDay() + 1));
}

export function getFridayWeek(d: Date) {
    d = new Date(d);
    return new Date(d.setDate(d.getDate() - d.getDay() + 5));
}

export function getWeekString(date: Date) {
    const start = getMondayWeek(date);
    const end = getFridayWeek(date);

    if (start.getFullYear() === end.getFullYear())
        if (start.getMonth() === end.getMonth())
            return `${start.getFullYear()} ${getMonthShortName(start.getMonth())} ${pad(start.getDate(), 2)} - ${pad(end.getDate(), 2)}`
        else
            return `${start.getFullYear()} ${getMonthShortName(start.getMonth())} ${pad(start.getDate(), 2)} - ${getMonthShortName(end.getMonth())} ${pad(end.getDate(), 2)}`
    else
        return `${start.getFullYear()} ${getMonthShortName(start.getMonth())} ${pad(start.getDate(), 2)} - ${start.getFullYear()} ${getMonthShortName(end.getMonth())} ${pad(end.getDate(), 2)}`
}

export function getMonthShortName(monthNo: number, def?: boolean) {
    const date = new Date();
    date.setMonth(monthNo);

    return date.toLocaleString(def ? 'en' : navigator.language, { month: 'short' });
}

export function getEnMonthShortName(monthNo: number) {
    const date = new Date();
    date.setMonth(monthNo);

    return date.toLocaleString('en', { month: 'short' });
}

export const areEqualsDates = (a: Date|undefined|null, b:Date|undefined|null) => 
    (a ? getInvariantDateString(a) : 'none') === (b ? getInvariantDateString(b) : 'none');